import * as Sentry from '@sentry/react'
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider as TanstackQueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AxiosError, isCancel } from 'axios'

import { Toast } from 'src/app/core/services/toast'
import { isAuthenticationExpiryError } from 'src/interfaces/http/errors'

const isOfflineNetworkError = (error: unknown): boolean => {
  return (
    error instanceof AxiosError &&
    (error.code === 'ERR_NETWORK' || error.code === 'ERR_NETWORK_OFFLINE') &&
    !navigator.onLine
  )
}

const isTransientNetworkError = (error: unknown): boolean => {
  return (
    error instanceof AxiosError &&
    (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')
  )
}

export const shouldRetryQuery = (
  failureCount: number,
  error: unknown,
): boolean => {
  if (isAuthenticationExpiryError(error) || isCancel(error)) return false

  // A short client/edge interruption can make every Vault request fail at once.
  // Give network failures enough time to recover while retaining the existing
  // single retry for application and HTTP errors.
  return isTransientNetworkError(error) ? failureCount < 3 : failureCount < 1
}

const handleError = (error: unknown, meta?: Record<string, unknown>): void => {
  if (meta?.silent) return
  if (isOfflineNetworkError(error)) return
  // Authentication expiry is handled centrally by the auth interceptor. Do
  // not misreport it as a backend failure while the router returns to login.
  if (isAuthenticationExpiryError(error) || isCancel(error)) return

  const status =
    error instanceof AxiosError ? error.response?.status : undefined
  if (!(error instanceof AxiosError) || status === undefined || status >= 500) {
    Sentry.withScope(scope => {
      scope.setTag('error.source', 'react-query')
      if (status !== undefined) scope.setTag('http.status_code', status)
      if (error instanceof AxiosError && error.code) {
        scope.setTag('axios.code', error.code)
      }
      Sentry.captureException(error)
    })
  }

  const message = error instanceof Error ? error.message : 'Unexpected error'
  Toast.notify({ message, type: Toast.toastType.ERROR })
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: shouldRetryQuery,
      retryDelay: attemptIndex => Math.min(1_000 * 2 ** attemptIndex, 4_000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => handleError(error, query.meta),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      handleError(error, mutation.meta),
  }),
})

export const QueryClientProvider = ({
  children,
}: {
  children: React.ReactNode
}) => (
  <TanstackQueryClientProvider client={queryClient}>
    {children}
    {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
  </TanstackQueryClientProvider>
)
